/**
 * F 232 — the Shiki highlighter handed to BlockNote's `codeBlock`.
 *
 * BlockNote 0.47.3 takes `createHighlighter: () => Promise<HighlighterGeneric>`
 * (see `node_modules/@blocknote/core/types/src/blocks/Code/block.d.ts`).
 * It ships `@shikijs/types` — the TYPE — but no runtime, so nothing is
 * highlighted until an implementation is provided. Stage 1 measured the
 * shipped block: one distinct text colour across the whole body, i.e.
 * monochrome. This file is what makes that stop being true.
 *
 * ONE instance, memoised. Shiki's `createHighlighter` is expensive —
 * grammars are compiled on load — and `useCreateBlockNote` is called on
 * every render of the editor component, so an unmemoised factory would
 * rebuild the highlighter on every keystroke-adjacent re-render.
 */
import { createHighlighter, createJavaScriptRegexEngine, type HighlighterGeneric } from 'shiki';
import { LANGUAGE_IDS } from './codeLanguages';

type HL = HighlighterGeneric<any, any>;

let instance: Promise<HL> | null = null;

/**
 * F 240 — the theme pair, and why the app can choose it.
 *
 * WHY A PAIR, NOT ONE THEME. Measured before this change: the block
 * rendered IDENTICALLY in light and dark mode — canvas `rgb(22,22,22)`
 * in both, tokens identical in both, `--shiki-light`/`--shiki-dark` never
 * emitted at all. BlockNote never passes `theme` or `defaultColor` to
 * Shiki (neither string appears in its bundle), and `codeToTokens`
 * requires one, so Shiki resolved to the FIRST loaded theme and stuck
 * there. Loading a second theme changed nothing; nothing selected it.
 *
 * WHY github-light + github-dark. Already loaded, so no new grammars and
 * no bundle growth; same family, so light and dark are visually the same
 * design; and each has a matching canvas it was designed against —
 * `#fff` light, `#24292e` dark.
 *
 * HOW IT IS APPLIED. `withDualTheme` wraps the instance BlockNote holds
 * and injects the pair plus `defaultColor: false`, which makes Shiki emit
 * per-token CSS VARIABLES (`--shiki-light` / `--shiki-dark`) instead of one
 * baked-in colour. The swap then happens in CSS, where the page theme
 * already lives. Nothing here paints a token colour by hand.
 */
export const CODE_THEMES = { light: 'github-light', dark: 'github-dark' } as const;

type ThemeMethod = 'codeToTokens' | 'codeToTokensBase' | 'codeToHtml' | 'codeToHtmlBase';

const THEME_METHODS = new Set<string>(['codeToTokens', 'codeToTokensBase', 'codeToHtml', 'codeToHtmlBase']);

function withDualTheme(hl: HL): HL {
  return new Proxy(hl, {
    get(target, prop) {
      const value = Reflect.get(target, prop, target);
      if (typeof value !== 'function' || !THEME_METHODS.has(prop as string)) return value;
      return (...args: unknown[]) => {
        // `codeToTokensBase(primitive, code, options)` is three-arg; the
        // others are two-arg. The options object is always LAST, so
        // rewriting the last argument covers both without a branch that
        // could pick the wrong one.
        const idx = args.length - 1;
        const opts = (args[idx] ?? {}) as Record<string, unknown>;
        const next = [...args];
        next[idx] = { ...opts, themes: CODE_THEMES, defaultColor: false };
        return (value as (...a: unknown[]) => unknown).apply(target, next);
      };
    },
  });
}

/**
 * Shiki loads grammars asynchronously. The editor must not block on it:
 * a code block that waits for a 200ms compile to show its first frame is
 * worse than one that shows unhighlighted text and sharpens a moment
 * later, so a failure here degrades to the current monochrome rendering
 * rather than throwing.
 */
export function getCodeHighlighter(): Promise<HL> {
  if (!instance) {
    // The JS regex engine, not the default WASM one. Measured: the WASM
    // engine threw `CompileError: WebAssembly.instantiate()` and the block
    // stayed monochrome with only a console warning to show for it. The
    // grammar data is the same; only the matcher differs, and the JS engine
    // is ~0 bytes of extra download and does not depend on the host serving
    // a .wasm with the right MIME type.
    instance = createHighlighter({
      themes: ['github-light', 'github-dark'],
      langs: [...LANGUAGE_IDS],
      engine: createJavaScriptRegexEngine(),
    })
      // Wrapped, not replaced: BlockNote still needs getLoadedLanguages()
      // and loadLanguage() off the same object, and they pass straight
      // through. The failure mode is swallowed rather than thrown, because a
      // code block that cannot be highlighted must still render.
      .then(withDualTheme).catch((err) => {
      // A dead highlighter must not take the editor with it.
      console.warn('[f232] shiki highlighter unavailable; code blocks stay monochrome:', err);
      throw err;
    });
  }
  return instance;
}

/** Test seam: drop the cached instance so a test can build a fresh one. */
export function resetCodeHighlighter(): void {
  instance = null;
}

/** True when the real highlighter is in place (not the catch path). */
export async function isHighlighterReady(): Promise<boolean> {
  try {
    const hl = await getCodeHighlighter();
    return typeof hl.codeToHtml === 'function';
  } catch {
    return false;
  }
}
